import React, { useReducer } from "react";
import { hot } from "react-hot-loader";
import Fuse from "fuse.js";
import Slider from "react-rangeslider";
import "react-rangeslider/lib/index.css";
import uniq from "lodash/uniq";
import isNumber from "lodash/isNumber";
import isNaN from "lodash/isNaN";

const fuse = new Fuse(window.isolaData.products, {
  includeScore: true,
  keys: [
    "title",
    "excerpt",
    "content",
    "tg",
    "td",
    "dk",
    "df",
    "regions",
    "attributes",
    "markets.title",
    "slashsheets",
  ],
});

const primaryMenuItems = [
  { title: "All Products" },
  {
    title: "By Attribute",
    facets: window.isolaData.attributes,
  },
  {
    title: "By Market",
    facets: window.isolaData.markets,
  },
  { title: "Advanced Search" },
];

function getSearchConfig({
  searchText,
  attributes,
  markets,
  regions,
  slashsheets,
  tg,
  td,
  dk,
  df,
}) {
  const config = {};

  const textSearch = searchText
    ? {
        $or: [
          { title: searchText },
          { excerpt: searchText },
          { content: searchText },
        ],
      }
    : null;

  const checkedRegions = regions
    .filter((r) => r.checked)
    .map((r) => ({ regions: r.title }));
  const checkedAttributes = attributes
    .filter((r) => r.checked)
    .map((r) => ({ attributes: r.title }));
  const checkedMarkets = markets
    .filter((r) => r.checked)
    .map((r) => ({ "markets.title": r.title }));
  const checkedSlashsheets = slashsheets
    .filter((r) => r.checked)
    .map((r) => ({ slashsheets: r.title }));

  const $and = [
    ...checkedRegions,
    ...checkedAttributes,
    ...checkedMarkets,
    ...checkedSlashsheets,
  ];

  if (td) {
    $and.push({ td: td.toString() });
  }

  if (tg) {
    $and.push({ tg: tg.toString() });
  }

  if (dk) {
    $and.push({ dk: dk.toString() });
  }

  if (df) {
    $and.push({ df: df.toString() });
  }

  if (textSearch) {
    $and.push(textSearch);
  }

  if ($and.length) {
    config.$and = $and;
  }

  return config;
}

const initialState = {
  primaryMenu: 0,
  secondaryMenu: 0,
  searchText: "",
  searchConfig: {},
  attributes: window.isolaData.attributes,
  markets: window.isolaData.markets,
  regions: window.isolaData.regions.map((r) => ({ title: r, id: r })),
  slashsheets: window.isolaData.slashsheets,
  searchResults: window.isolaData.products,
  tg: 0,
  td: 0,
  dk: 0,
  df: 0,
  uniqueTgValues: uniq(
    window.isolaData.products
      .map((p) => parseFloat(p.tg))
      .filter((v) => !isNaN(v) && isNumber(v))
  ),
  uniqueTdValues: uniq(
    window.isolaData.products
      .map((p) => parseFloat(p.td))
      .filter((v) => !isNaN(v) && isNumber(v))
  ),
  uniqueDkValues: uniq(
    window.isolaData.products
      .map((p) => parseFloat(p.dk))
      .filter((v) => !isNaN(v) && isNumber(v))
  ),
  uniqueDfValues: uniq(
    window.isolaData.products
      .map((p) => parseFloat(p.df))
      .filter((v) => !isNaN(v) && isNumber(v))
  ),
};

const ACTION_TYPES = {
  SET_PRIMARY_MENU: "set_primary_menu",
  SET_SECONDARY_MENU: "set_secondary_menu",
  RESET_SEARCH_RESULTS: "reset_search_results",
  SET_SEARCH_RESULTS: "set_search_results",
  SET_SEARCH_TEXT: "set_search_text",
  TOGGLE_CHECKBOX_BY_ID_TYPE: "toggle_checkbox_by_id_type",
  TOGGLE_ALL_CHECKBOX_TYPE: "toggle_all_checkbox_type",
  SET_TG_VALUE: "set_tg_value",
  SET_TD_VALUE: "set_td_value",
  SET_DK_VALUE: "set_dk_value",
  SET_DF_VALUE: "set_df_value",
};

function snapTo(value, values) {
  const [_, cur] = values.reduce((col, cur) => {
    const dif = Math.abs(value - cur);
    if (col === -1) {
      return [dif, cur];
    }

    if (col[0] > dif) {
      return [dif, cur];
    }
    return col;
  }, -1);
  return cur;
}

function reducer(state, action) {
  const newState = { ...state };
  switch (action.type) {
    case ACTION_TYPES.SET_PRIMARY_MENU:
      newState.primaryMenu = action.payload;
      break;
    case ACTION_TYPES.SET_SECONDARY_MENU:
      newState.secondaryMenu = action.payload;
      break;
    case ACTION_TYPES.RESET_SEARCH_RESULTS:
      newState.searchResults = initialState.searchResults;
      newState.searchText = initialState.searchText;
      newState.searchConfig = initialState.searchConfig;
      newState.attributes = initialState.attributes;
      newState.markets = initialState.markets;
      newState.regions = initialState.regions;
      newState.slashsheets = initialState.slashsheets;
      newState.tg = initialState.tg;
      newState.td = initialState.td;
      newState.df = initialState.df;
      return newState;
    case ACTION_TYPES.SET_SEARCH_RESULTS:
      newState.searchResults = action.payload;
      return newState;
    case ACTION_TYPES.SET_SEARCH_TEXT:
      newState.searchText = action.payload;
      break;
    case ACTION_TYPES.TOGGLE_CHECKBOX_BY_ID_TYPE:
      newState[action.payload.key] = state[action.payload.key].map((a) => ({
        ...a,
        checked: action.payload.id === a.id ? !a.checked : a.checked,
      }));
      break;
    case ACTION_TYPES.TOGGLE_ALL_CHECKBOX_TYPE:
      newState[action.payload.key] = state[action.payload.key].map((a) => ({
        ...a,
        checked: action.payload.checked,
      }));
      break;
    case ACTION_TYPES.SET_TG_VALUE:
      newState.tg = snapTo(action.payload, state.uniqueTgValues);
      break;
    case ACTION_TYPES.SET_TD_VALUE:
      newState.td = snapTo(action.payload, state.uniqueTdValues);
      break;
    case ACTION_TYPES.SET_DK_VALUE:
      newState.dk = snapTo(action.payload, state.uniqueDkValues);
      break;
    case ACTION_TYPES.SET_DF_VALUE:
      newState.df = snapTo(action.payload, state.uniqueDfValues);
      break;
  }

  const searchConfig = getSearchConfig(newState);

  if (searchConfig.$and) {
    newState.searchResults = fuse.search(searchConfig);
  } else {
    newState.searchResults = initialState.searchResults;
  }

  return newState;
}

const Menu = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const buttonHoverClasses =
    "text-theme-gray hover:from-theme-orange-grad hover:to-transparent hover:border-theme-orange hover:text-white";
  const buttonActiveClasses =
    "from-theme-orange-grad to-transparent border-theme-orange text-white";

  return (
    <div className="absolute w-full lg:inset-0 lg:h-full z-20 lg:overflow-hidden left-0 product-nav-wrapper mt-10 md:mt-5 border-t-2">
      <div className="product-navigation flex flex-col lg:flex-row h-full w-full">
        <div className="absolute right-0 p-3"><i className="far fa-times text-white"></i></div>
        <div className="bg-gray-800 border-l-6 border-gray-800 flex-shrink-0 pb-6 pt-4 lg:pt-10 xl:pl-12 pr-4 lg:pr-0">
          <h2 className="text-2xl px-6 lg:px-12 border-l-6 border-gray-800 text-white mb-2 lg:mb-6">
            Our Products
          </h2>
          <p className="font-bold  px-6 lg:px-12 border-l-6 border-gray-800 text-white">
            Filter By:
          </p>

          <div className="px-6 lg:px-12 border-l-6 border-gray-800">
            <select
              className="block lg:hidden w-full p-2"
              onChange={(e) => {
                dispatch({
                  type: ACTION_TYPES.SET_PRIMARY_MENU,
                  payload: parseInt(e.target.value),
                });
                dispatch({
                  type: ACTION_TYPES.SET_SECONDARY_MENU,
                  payload: 0,
                });
                dispatch({ type: ACTION_TYPES.RESET_SEARCH_RESULTS });
              }}
            >
              {primaryMenuItems.map((item, idx) => {
                return (
                  <option
                    key={item.title}
                    value={idx}
                    dangerouslySetInnerHTML={{ __html: item.title }}
                  />
                );
              })}
            </select>
          </div>

          <ul className="hidden lg:block">
            {primaryMenuItems.map((item, idx) => {
              const isActive = idx === state.primaryMenu;
              return (
                <li key={item.title}>
                  <button
                    onClick={() => {
                      dispatch({
                        type: ACTION_TYPES.SET_PRIMARY_MENU,
                        payload: idx,
                      });
                      dispatch({
                        type: ACTION_TYPES.SET_SECONDARY_MENU,
                        payload: 0,
                      });
                      dispatch({ type: ACTION_TYPES.RESET_SEARCH_RESULTS });
                    }}
                    className={`block w-full text-left focus:outline-none leading-10 border-l-6 border-gray-800 pl-12 bg-gradient-to-r ${
                      isActive ? buttonActiveClasses : buttonHoverClasses
                    }`}
                  >
                    <span dangerouslySetInnerHTML={{ __html: item.title }} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {primaryMenuItems[state.primaryMenu].facets && (
          <div className="bg-gray-300 pt-8 px-4 flex-initial flex-shrink-0">
            <p className="font-bold leading-8 mb-2 px-6 lg:px-12">
              {primaryMenuItems[state.primaryMenu].title}
            </p>

            <div className="mb-10 px-6 lg:px-12 lg:hidden">
              <select
                className="block w-full p-2"
                onChange={(e) => {
                  const idx = e.target.value;
                  const facet = primaryMenuItems[state.primaryMenu].facets[idx];
                  const searchKey = facet.post_type + "s";
                  dispatch({
                    type: ACTION_TYPES.SET_SECONDARY_MENU,
                    payload: facet.id,
                  });
                  dispatch({
                    type: ACTION_TYPES.SET_SEARCH_RESULTS,
                    payload: fuse.search({ [searchKey]: facet.title }),
                  });
                }}
              >
                {primaryMenuItems[state.primaryMenu].facets.map(
                  (facet, idx) => {
                    return (
                      <option
                        key={facet.id}
                        value={idx}
                        dangerouslySetInnerHTML={{ __html: facet.title }}
                      />
                    );
                  }
                )}
              </select>
            </div>

            <ul className="hidden lg:block">
              {primaryMenuItems[state.primaryMenu].facets.map((facet) => {
                const selected = state.secondaryMenu === facet.id;
                const searchKey = facet.post_type + "s";
                return (
                  <li key={facet.id}>
                    <button
                      onClick={() => {
                        dispatch({
                          type: ACTION_TYPES.SET_SECONDARY_MENU,
                          payload: facet.id,
                        });
                        dispatch({
                          type: ACTION_TYPES.SET_SEARCH_RESULTS,
                          payload: fuse.search({ [searchKey]: facet.title }),
                        });
                      }}
                      className={`${
                        selected && "underline"
                      } focus:outline-none block text-theme-gray text-sm font-medium leading-8`}
                    >
                      <span dangerouslySetInnerHTML={{ __html: facet.title }} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="bg-ultra-light-gray flex-initial flex-grow relative overflow-y-scroll product-list-wrap">
          {state.primaryMenu === 3 && (
            <AdvancedSearchForm
              dispatch={dispatch}
              attributes={state.attributes}
              markets={state.markets}
              regions={state.regions}
              slashsheets={state.slashsheets}
              tg={state.tg}
              td={state.td}
              dk={state.dk}
              df={state.df}
              uniqueTgValues={state.uniqueTgValues}
              uniqueTdValues={state.uniqueTdValues}
              uniqueDkValues={state.uniqueDkValues}
              uniqueDfValues={state.uniqueDfValues}
            />
          )}

          <ProductsSearch
            products={state.searchResults}
            searchText={state.searchText}
            attributes={state.attributes}
            markets={state.markets}
            regions={state.regions}
            slashsheets={state.slashsheets}
          />
        </div>
      </div>
    </div>
  );
};

export default hot(module)(Menu);

function ProductsSearch({ products }) {
  return (
    <>
      <div className="p-4 flex flex-wrap">
        {products.map((p) => {
          const product = p.item || p;
          return (
            <div
              key={product.id}
              className="w-full md:w-1/2 xl:w-1/3 bg-white product-listing flex flex-col place-content-between relative"
            >
              <div className="heading-wrap z-1">
                <h2
                  className=""
                  dangerouslySetInnerHTML={{ __html: product.title }}
                />
              </div>
              <div className="bg-white self-stretch details-wrap pb-4">
                <div className="flex flex-wrap">
                  <div className="flex-grow">
                    <div className="product-tg block lg:inline-block px-2 xl:px-4 py-4">
                      <strong>Tg: </strong>
                      <span
                        className=""
                        dangerouslySetInnerHTML={{ __html: product.tg }}
                      />
                      &#176;
                    </div>
                    <div className="product-td block lg:inline-block px-2 xl:px-4 py-4">
                      <strong>Td: </strong>
                      <span
                        className=""
                        dangerouslySetInnerHTML={{ __html: product.td }}
                      />
                      &#176;
                    </div>
                  </div>
                  <div className="flex-grow">
                    <div className="product-dk block lg:inline-block px-2 xl:px-4 py-4">
                      <strong>Dk: </strong>
                      <span
                        className=""
                        dangerouslySetInnerHTML={{ __html: product.dk }}
                      />
                    </div>

                    <div className="product-df block lg:inline-block px-2 xl:px-4 py-4">
                      <strong>Df: </strong>
                      <span
                        className=""
                        dangerouslySetInnerHTML={{ __html: product.df }}
                      />
                    </div>
                  </div>
                </div>

                <hr className="pb-5" />

                {product.markets && (
                  <>
                    <h3 className="px-4 mb-5">Markets</h3>
                    <div className="flex flex-wrap flex-row justify-around mb-10">
                      {product.markets.map((m) => {
                        if (!m.market_icon) {
                          return null;
                        }
                        return (
                          <div
                            key={m.id}
                            className="product-markets h-16 w-16 m-2"
                          >
                            <img src={m.market_icon} alt="market icon" />
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
              <a
                href={product.link}
                className="view-details block py-3 px-3 text-center bg-red z-1"
              >
                View Details
              </a>
            </div>
          );
        })}
      </div>
    </>
  );
}

function AdvancedSearchForm({
  dispatch,
  attributes,
  markets,
  regions,
  slashsheets,
  tg,
  td,
  dk,
  df,
  uniqueTgValues,
  uniqueTdValues,
  uniqueDkValues,
  uniqueDfValues,
}) {
  const handleChange = (e) => {
    dispatch({
      type: ACTION_TYPES.SET_SEARCH_TEXT,
      payload: e.target.value,
    });
  };

  const maxTg = Math.max(...uniqueTgValues);
  const maxTd = Math.max(...uniqueTdValues);
  const maxDk = Math.max(...uniqueDkValues);
  const maxDf = Math.max(...uniqueDfValues);

  const minTg = Math.min(...uniqueTgValues);
  const minTd = Math.min(...uniqueTdValues);
  const minDk = Math.min(...uniqueDkValues);
  const minDf = Math.min(...uniqueDfValues);

  return (
    <div className="p-10 bg-white">
      <label htmlFor="search" className="flex items-center mb-5">
        <span className="mb-1 pr-4 font-sans text-lg font-bold tracking-wide uppercase text-theme-gray">
          Search:
        </span>
        <input
          className="w-full px-4 py-3 font-sans leading-tight bg-white border border-gray-200 appearance-none focus:outline-none focus:bg-white focus:border-gray-500"
          onChange={handleChange}
          type="text"
          id="search"
        />
      </label>

      <div className="flex flex-col md:flex-row flex-wrap p-5 border border-gray-200">
        <div className="p-5 bg-light-gray w-full md:w-1/2 lg:w-1/4 border-b-4 border-r-4 border-white attributes">
          <h2 className="text-xl text-center mb-2">Attributes</h2>
          {attributes.map((facet) => {
            return (
              <Checkbox
                key={facet.title}
                id={facet.title}
                className="flex items-center font-medium leading-8"
                onChange={() => {
                  dispatch({
                    type: ACTION_TYPES.TOGGLE_CHECKBOX_BY_ID_TYPE,
                    payload: {
                      id: facet.id,
                      key: "attributes",
                    },
                  });
                }}
                checked={!!facet.checked}
                text={facet.title}
              />
            );
          })}

          <Checkbox
            className="flex items-center font-medium leading-8"
            onChange={(e) => {
              dispatch({
                type: ACTION_TYPES.TOGGLE_ALL_CHECKBOX_TYPE,
                payload: {
                  checked: e.target.checked,
                  key: "attributes",
                },
              });
            }}
            text="Select All"
          />
        </div>
        <div className="p-5 bg-light-gray w-full md:w-1/2 lg:w-1/4 border-b-4 border-r-4 border-white market-segment">
          <h2 className="text-xl text-center mb-2">Market Segment</h2>
          {markets.map((facet) => {
            return (
              <Checkbox
                key={facet.title}
                className="flex items-center font-medium leading-8"
                onChange={() => {
                  dispatch({
                    type: ACTION_TYPES.TOGGLE_CHECKBOX_BY_ID_TYPE,
                    payload: {
                      id: facet.id,
                      key: "markets",
                    },
                  });
                }}
                checked={!!facet.checked}
                text={facet.title}
              />
            );
          })}

          <Checkbox
            className="flex items-center font-medium leading-8"
            onChange={(e) => {
              dispatch({
                type: ACTION_TYPES.TOGGLE_ALL_CHECKBOX_TYPE,
                payload: {
                  checked: e.target.checked,
                  key: "markets",
                },
              });
            }}
            text="Select All"
          />
        </div>
        <div className="flex flex-wrap w-full md:w-1/2">
          <div className="p-5 bg-light-gray w-1/2 border-b-4 border-r-4 border-white thermal-performance">
            <h2 className="text-xl text-center mb-2">Thermal Performance</h2>

            <div className="label">Tg: > 100&deg;C</div>
            <Slider
              min={minTg}
              max={maxTg}
              value={tg}
              orientation="horizontal"
              onChange={(val) => {
                dispatch({
                  type: ACTION_TYPES.SET_TG_VALUE,
                  payload: val,
                });
              }}
            />

            <div className="label">Td: > 300&deg;C</div>
            <Slider
              min={minTd}
              max={maxTd}
              value={td}
              orientation="horizontal"
              onChange={(val) => {
                dispatch({
                  type: ACTION_TYPES.SET_TD_VALUE,
                  payload: val,
                });
              }}
            />
          </div>
          <div className="p-5 bg-light-gray w-1/2 border-b-4 border-white electrical-performance">
            <h2 className="text-xl text-center mb-2">Electrical Performance</h2>

            <div className="label">Dk: > 4.5</div>
            <Slider
              min={minDk}
              max={maxDk}
              step={0.01}
              value={dk}
              orientation="horizontal"
              onChange={(val) => {
                dispatch({
                  type: ACTION_TYPES.SET_DK_VALUE,
                  payload: val,
                });
              }}
            />
            <div className="label">Df: > 0.033</div>
            <Slider
              min={minDf}
              max={maxDf}
              step={0.001}
              value={df}
              orientation="horizontal"
              onChange={(val) => {
                dispatch({
                  type: ACTION_TYPES.SET_DF_VALUE,
                  payload: val,
                });
              }}
            />
          </div>

          <div className="p-5 w-full border-b-4 border-white bg-light-gray region">
            <h2 className="text-xl text-center mb-2">Region</h2>

            <div className="flex flex-wrap">
              {regions.map((facet) => {
                return (
                  <Checkbox
                    key={facet.title}
                    className="flex items-center font-medium leading-8 w-1/2"
                    onChange={() => {
                      dispatch({
                        type: ACTION_TYPES.TOGGLE_CHECKBOX_BY_ID_TYPE,
                        payload: {
                          id: facet.id,
                          key: "regions",
                        },
                      });
                    }}
                    checked={!!facet.checked}
                    text={facet.title}
                  />
                );
              })}

              <Checkbox
                className="flex items-center font-medium leading-8 w-1/2"
                onChange={(e) => {
                  dispatch({
                    type: ACTION_TYPES.TOGGLE_ALL_CHECKBOX_TYPE,
                    payload: {
                      checked: e.target.checked,
                      key: "regions",
                    },
                  });
                }}
                text="Select All"
              />
            </div>
          </div>
        </div>

        <div className="p-5 w-full md:w-1/2 border-r-4 border-white bg-light-gray slash-sheet">
          <h2 className="text-xl text-center mb-2">Slash Sheet</h2>

          <div className="flex flex-wrap">
            {slashsheets.map((facet) => {
              return (
                <Checkbox
                  key={facet.title}
                  className="flex items-center font-medium leading-8 w-1/6"
                  onChange={() => {
                    dispatch({
                      type: ACTION_TYPES.TOGGLE_CHECKBOX_BY_ID_TYPE,
                      payload: {
                        id: facet.id,
                        key: "slashsheets",
                      },
                    });
                  }}
                  checked={!!facet.checked}
                  text={facet.title}
                />
              );
            })}

            <Checkbox
              className="flex items-center font-medium leading-8 w-1/6"
              onChange={(e) => {
                dispatch({
                  type: ACTION_TYPES.TOGGLE_ALL_CHECKBOX_TYPE,
                  payload: {
                    checked: e.target.checked,
                    key: "slashsheets",
                  },
                });
              }}
              text="Select All"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Checkbox({ id, text, checked, onChange, className }) {
  const handleChange = (e) => {
    onChange(e, id);
  };

  return (
    <label className={className}>
      <input
        type="checkbox"
        className="mr-2"
        checked={checked}
        onChange={handleChange}
      />
      <span dangerouslySetInnerHTML={{ __html: text }} />
    </label>
  );
}
